import { GoogleGenAI, Type } from "@google/genai";
import { SubtitleResponse, Subtitle } from "../types";
import { shiftSubtitles } from "../utils";

// Declare process to avoid TypeScript errors when accessing process.env.API_KEY
declare var process: any;

// Initialize Gemini Client
const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

// Internal helper for service
const timeToSeconds = (timeStr: string): number => {
  if (!timeStr) return 0;
  const cleanStr = timeStr.replace(',', '.');
  const parts = cleanStr.split(':').map(parseFloat);
  if (parts.length === 3) return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
  if (parts.length === 2) return (parts[0] * 60) + parts[1];
  return 0;
};

const secondsToTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const milliseconds = Math.round((totalSeconds % 1) * 1000);
  const pad = (num: number, size: number) => num.toString().padStart(size, '0');
  return `${pad(hours, 2)}:${pad(minutes, 2)}:${pad(seconds, 2)},${pad(milliseconds, 3)}`;
};

const repairSubtitles = (subtitles: Subtitle[]): Subtitle[] => {
  return subtitles.map((sub, index, arr) => {
    let startSec = timeToSeconds(sub.startTime);
    let endSec = sub.endTime ? timeToSeconds(sub.endTime) : 0;

    // Sanity Fix 1: If End is before Start, fix it.
    if (endSec <= startSec) {
        if (index < arr.length - 1) {
            const nextStart = timeToSeconds(arr[index + 1].startTime);
            if (nextStart > startSec) {
                endSec = Math.min(startSec + 5, nextStart - 0.05); 
            } else {
                endSec = startSec + 2.0; 
            }
        } else {
            endSec = startSec + 2.0; 
        }
    }

    // Sanity Fix 2: Overlaps
    if (index < arr.length - 1) {
        const nextStart = timeToSeconds(arr[index + 1].startTime);
        if (endSec > nextStart) {
             endSec = Math.max(startSec + 0.5, nextStart - 0.05);
        }
    }

    return {
      ...sub,
      startTime: secondsToTime(startSec),
      endTime: secondsToTime(endSec)
    };
  });
};

export const generateSubtitlesFromVideo = async (
  base64Data: string,
  mimeType: string,
  targetLanguage: string = "English",
  maxWords: number = 8,
  detectedAudioOnset: number = 0,
  useProModel: boolean = false,
  videoDuration: number = 0 // Added precise duration constraint
): Promise<SubtitleResponse> => {
  try {
    if (!process.env.API_KEY) {
      throw new Error("API Key is missing. Please set API_KEY in your environment variables.");
    }

    // Select model based on preference
    // gemini-3-pro-preview for complex reasoning/timing
    // gemini-3-flash-preview for speed/cost efficiency
    const model = useProModel ? "gemini-3-pro-preview" : "gemini-3-flash-preview";
    
    const isAudio = mimeType.startsWith('audio');
    const inputType = isAudio ? "audio file" : "video";

    console.log(`Generating subtitles using model: ${model}, duration constraint: ${videoDuration}s`);

    const prompt = `
      You are a professional subtitle timing engine.
      
      INPUT CONTEXT:
      - Media Type: ${inputType}
      - TOTAL DURATION: ${videoDuration.toFixed(2)} seconds.
      - DETECTED SPEECH START: Approx ${detectedAudioOnset.toFixed(2)} seconds.

      YOUR TASK:
      Generate precise SRT timestamps.

      CRITICAL TIMING RULES:
      1. **ABSOLUTE LIMIT:** The final "endTime" MUST NOT exceed ${videoDuration.toFixed(2)} seconds.
      2. **PROPORTIONALITY:** Distribute the text evenly across the spoken duration. Do not cram everything at the beginning.
      3. **SILENCE HANDLING:** If there is no speech, do not generate a subtitle. Gaps are expected.
      4. **DRIFT PREVENTION:** Treat the audio as a linear timeline. 50% of the text should likely appear around the ${(videoDuration / 2).toFixed(2)} second mark.

      TEXT RULES:
      - Max words per subtitle: ${maxWords}.
      - Split on sentence boundaries.
      - Translate strictly to: ${targetLanguage}.

      OUTPUT FORMAT (JSON):
      {
        "detectedLanguage": "Language",
        "subtitles": [
          { "id": 1, "startTime": "00:00:00,500", "endTime": "00:00:03,000", "text": "...", "translatedText": "..." }
        ]
      }
    `;

    const response = await ai.models.generateContent({
      model: model,
      contents: {
        parts: [
          {
            inlineData: {
              mimeType: mimeType,
              data: base64Data,
            },
          },
          {
            text: prompt,
          },
        ],
      },
      config: {
        responseMimeType: "application/json",
        temperature: 0.1, // Low temp for precision
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            detectedLanguage: { type: Type.STRING },
            subtitles: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  id: { type: Type.INTEGER },
                  startTime: { type: Type.STRING },
                  endTime: { type: Type.STRING },
                  text: { type: Type.STRING },
                  translatedText: { type: Type.STRING },
                },
                required: ["id", "startTime", "endTime", "text"]
              },
            },
          },
          required: ["subtitles", "detectedLanguage"]
        },
      },
    });

    if (!response.text) {
      throw new Error("No response generated from AI Service.");
    }

    const data = JSON.parse(response.text) as SubtitleResponse;
    let finalSubtitles = repairSubtitles(data.subtitles);

    // Initial Shift based on detected onset (Fix start offset)
    if (finalSubtitles.length > 0 && detectedAudioOnset > 0) {
       const aiFirstStart = timeToSeconds(finalSubtitles[0].startTime);
       const shiftAmount = detectedAudioOnset - aiFirstStart;
       
       // Only apply if the discrepancy is > 0.5s but less than 10 mins (sanity check)
       if (Math.abs(shiftAmount) > 0.5 && Math.abs(shiftAmount) < 600) {
           console.log(`Auto-Sync: Shifting by ${shiftAmount.toFixed(2)}s to match onset.`);
           finalSubtitles = shiftSubtitles(finalSubtitles, shiftAmount);
       }
    }

    return {
      ...data,
      subtitles: finalSubtitles,
      usage: response.usageMetadata ? {
        promptTokenCount: response.usageMetadata.promptTokenCount || 0,
        candidatesTokenCount: response.usageMetadata.candidatesTokenCount || 0,
        totalTokenCount: response.usageMetadata.totalTokenCount || 0
      } : undefined
    };

  } catch (error: any) {
    console.error("Error generating subtitles:", error);
    throw error;
  }
};
